import React, { Component } from "react";
import { Link } from "react-router-dom";
import { AxiosResponse } from "axios";
import { CoursesResource } from "../../stores/CoursesStore/Types";
import { CurrentUserResource } from "../../stores/CurrentUserStore/Types";
import { getRequest } from "../../utils/httpRequest";
import NewCourse from "../Home/components/Courses/NewCourse";

interface Props {
  match: any;
  currentUser: CurrentUserResource;
}

interface State {
  featuredCourses: CoursesResource;
  loadedCourses: boolean;
  modalOpen: boolean;
}

class PassSuccess extends Component<Props, State> {
  state: State = {
    featuredCourses: [],
    loadedCourses: false,
    modalOpen: true,
  };

  componentDidMount() {
    document.body.classList.add("pass-success-modal-open");
    document.addEventListener("keydown", this.handleKeyDown);

    getRequest<CoursesResource, void>("/api/featured_courses.json")
      .then((response: AxiosResponse<CoursesResource>) => {
        const featuredCourses = response.data
          .filter((course) => !course.enrolled_student_present)
          .slice(0, 3);

        this.setState({ featuredCourses, loadedCourses: true });
      })
      .catch(() => {
        this.setState({ loadedCourses: true });
      });
  }

  componentWillUnmount() {
    document.body.classList.remove("pass-success-modal-open");
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.state.modalOpen) {
      this.closeModal();
    }
  };

  closeModal = () => {
    document.body.classList.remove("pass-success-modal-open");
    this.setState({ modalOpen: false });
  };

  closeModalFromScrim = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  };

  renderCheckMark = () => (
    <div className="pass-success__mark-wrap" aria-hidden="true">
      <span className="pass-success__mark">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 12l5 5L20 6" />
        </svg>
      </span>
      <span className="pass-success__spark pass-success__spark--a" />
      <span className="pass-success__spark pass-success__spark--b" />
      <span className="pass-success__spark pass-success__spark--c" />
    </div>
  );

  renderLockNote = () => (
    <div className="pass-success__lock-note">
      <span className="pass-success__lock-icon" aria-hidden="true">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="4" y="11" width="16" height="9" rx="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          <circle cx="12" cy="15.5" r="1.3" fill="currentColor" stroke="none" />
        </svg>
      </span>
      <span className="pass-success__lock-body">
        <h2>The price you paid is the price you keep</h2>
        <p>
          Locked in for as long as you stay. Prices drift upwards over the
          years. Yours will not.
        </p>
      </span>
    </div>
  );

  greetingName = () => {
    const { currentUser } = this.props;

    if (currentUser?.kind === "current-user") {
      return currentUser.short_name || currentUser.name?.split(" ")[0];
    }

    return "there";
  };

  greetingTime = () => {
    const hour = new Date().getHours();

    if (hour < 12) return "Morning";
    if (hour < 18) return "Afternoon";

    return "Evening";
  };

  // The same tiles the home page and course pages draw, so a new subscriber's
  // first three courses look like every other course on the site. This used
  // to draw its own cards from each course's wide banner image squashed into
  // a thumbnail, which looked like nothing else.
  renderFeaturedCourses = () => {
    const { featuredCourses, loadedCourses } = this.state;
    const { currentUser } = this.props;
    const userIsInUk =
      currentUser?.kind === "current-user" && Boolean(currentUser.is_in_uk);

    if (loadedCourses && featuredCourses.length === 0) return null;

    return (
      <main className="lh-body" id="lh-body">
        <p className="lh-greeting">
          {this.greetingTime()}, {this.greetingName()}
        </p>
        <p className="lh-strap">
          Pick anything that catches your eye and press start.
        </p>

        {!loadedCourses ? (
          <div className="lh-grid" aria-label="Loading courses">
            {[0, 1, 2].map((index) => (
              <div className="lh-card is-loading" key={index} />
            ))}
          </div>
        ) : (
          // .facelift-landing-page is where the tile styles live.
          <div className="facelift-landing-page lh-tiles">
            <div className="course-grid">
              {featuredCourses.map((course) => (
                <NewCourse
                  courseResource={course}
                  userIsInUk={userIsInUk}
                  key={course.id}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    );
  };

  renderCommunityBackdrop = () => (
    <main className="lh-body" id="lh-body">
      <p className="lh-greeting">
        {this.greetingTime()}, {this.greetingName()}.
      </p>
      <p className="lh-strap">Your Community Hub is ready when you are.</p>
      <div className="lh-community-card">
        <h2>Welcome to the community</h2>
        <p>
          Connect, join discussions, and stay in the loop with other curious
          learners.
        </p>
        <Link to="/community" className="pass-success__button">
          Go to community
        </Link>
      </div>
    </main>
  );

  render() {
    const slug = this.props.match.params.slug;
    const { modalOpen } = this.state;
    const isCommunity = slug === "community";
    const isLifetime = slug === "lifetime";
    const continuePath = isCommunity ? "/community" : "/profile";
    const closeLabel = isCommunity
      ? "Close and go to the community"
      : "Close and go to your courses";
    const title = isCommunity
      ? "Welcome to the Community Hub."
      : "The whole shelf is yours.";
    const lead = isCommunity ? (
      <>
        Your account is set up successfully, and you now have access to the{" "}
        <span className="pass-success__em">Community Hub</span>.
      </>
    ) : (
      <>
        Your <span className="pass-success__em">All Access Pass</span> is live,
        and that means the lot: every course, plus the Community Hub.
      </>
    );
    const how = isCommunity
      ? "Connect, join discussions, and stay in the loop with other curious learners."
      : "Pick one that catches your eye, hit Enrol now, and enjoy the learning.";

    return (
      <div className="ui pages pass-success" id="pass_success">
        <div className="pass-success__shell">
          {isCommunity
            ? this.renderCommunityBackdrop()
            : this.renderFeaturedCourses()}

          {modalOpen && (
            <div
              className="pass-success__scrim"
              onMouseDown={this.closeModalFromScrim}
            >
              <div
                className="pass-success__card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pass-success-title"
              >
                {isCommunity ? (
                  <Link
                    to={continuePath}
                    className="pass-success__close"
                    aria-label={closeLabel}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="pass-success__close"
                    aria-label={closeLabel}
                    onClick={this.closeModal}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </button>
                )}

                <p className="pass-success__eyebrow">Subscription confirmed</p>
                {this.renderCheckMark()}

                <h1 className="pass-success__title" id="pass-success-title">
                  {title}
                </h1>
                <p className="pass-success__lead">{lead}</p>
                <p className="pass-success__how">{how}</p>

                {isCommunity && (
                  <p className="pass-success__note">
                    Course enrolment is not included with your current access,
                    but you can upgrade anytime if you would like to start
                    learning through our courses.
                  </p>
                )}

                {!isLifetime && this.renderLockNote()}

                {isCommunity ? (
                  <Link to={continuePath} className="pass-success__button">
                    Go to community
                    <svg
                      className="pass-success__button-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 12h14" />
                      <path d="m13 6 6 6-6 6" />
                    </svg>
                  </Link>
                ) : (
                  <a
                    type="button"
                    href="/courses"
                    className="pass-success__button"
                  >
                    Start learning
                    <svg
                      className="pass-success__button-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 12h14" />
                      <path d="m13 6 6 6-6 6" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default PassSuccess;
